import { useLightMode } from '@/hooks/useLightMode'
import { useOpenIncident } from '@/hooks/useOpenIncident'
import { useTranslation } from 'react-i18next'

import {
  Box,
  IncidentCard,
  IncidentCardDescriptionText,
  Skeleton,
  VStack,
} from '@hivewatch/dark'

import {
  DeviceType,
  IncidentPriority,
  IncidentStatus,
} from '@/graphql/generated/schemas'
import { useAppDispatch } from '@/redux/hooks'
import { closeDeviceDrawer } from '@/redux/ui/uiSlice'
import { BUTTON_PRESS, trackEvent } from '@/utils/analytics'
import { formatDateTime } from '@/utils/formatDateTime'
import { toCardPriority, toCardStatus } from '@/utils/incidents'

import { getDeviceTypeIcon } from '../utils/getDeviceTypeIcon'

type DeviceAlertIncident = {
  id: string
  createdAt: string
  status?: IncidentStatus | null
  name?: string | null
  description?: string | null
  priority?: IncidentPriority | null
  displayId?: string | null
  facility?: { name?: string | null } | null
}

interface IProps {
  incidents: (DeviceAlertIncident | null | undefined)[]
  isLoading: boolean
  deviceType?: DeviceType
}

/**
 * Rebrand (ENG-32123, Phase 2) Recent Incidents tab. Renders each incident as
 * the shared dark `IncidentCard` (status pill, priority glyph, absolute
 * timestamp, `INC-ID · facility` line, leading device-type icon) — replacing
 * the legacy `EventListItem` colour-dot list.
 */
export const DarkDeviceAlerts = ({
  incidents,
  isLoading,
  deviceType,
}: IProps) => {
  const { t } = useTranslation()
  const colorScheme = useLightMode()
  const dispatch = useAppDispatch()
  const { openIncident } = useOpenIncident()

  // Goes through `useOpenIncident` rather than dispatching the open pair
  // directly: on /incidents the drawer gates on `?displayId`, so without
  // syncing it the previously opened incident's id holds the drawer in a
  // permanent skeleton (ENG-35869).
  const openAlertIncidentDrawer = (incident: DeviceAlertIncident) => {
    dispatch(closeDeviceDrawer())
    openIncident({ incidentId: incident.id, displayId: incident.displayId })
    trackEvent(`${BUTTON_PRESS} Device Drawer - Recent Incident`, {
      incident_id: incident.id,
    })
  }

  if (isLoading) {
    return (
      <VStack align='stretch' gap='12px' p='24px'>
        <Skeleton borderRadius='8px' h='96px' />
        <Skeleton borderRadius='8px' h='96px' />
        <Skeleton borderRadius='8px' h='96px' />
      </VStack>
    )
  }

  // Codegen types allow null edges/nodes, and useDeviceDrawerData maps them
  // through unfiltered — drop nullish entries before sorting/rendering.
  const validIncidents = incidents.filter(
    (incident): incident is DeviceAlertIncident => Boolean(incident)
  )

  if (!validIncidents.length) {
    return (
      <Box
        color={`${colorScheme === 'light' ? 'light.' : ''}text.secondary`}
        p='24px'
        textAlign='center'
      >
        {t('devices:drawer_no_incidents')}
      </Box>
    )
  }

  const sorted = [...validIncidents].sort(
    (a, b) => new Date(b.createdAt).valueOf() - new Date(a.createdAt).valueOf()
  )

  return (
    <VStack align='stretch' gap='12px' p='24px'>
      {sorted.map((incident) => {
        const description = [incident.displayId, incident.facility?.name]
          .filter(Boolean)
          .join(' · ')

        return (
          <IncidentCard
            colorScheme={colorScheme}
            cursor='pointer'
            description={
              description ? (
                <IncidentCardDescriptionText colorScheme={colorScheme}>
                  {description}
                </IncidentCardDescriptionText>
              ) : undefined
            }
            detectionTypeIcon={getDeviceTypeIcon(deviceType, colorScheme)}
            key={incident.id}
            onClick={() => openAlertIncidentDrawer(incident)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                openAlertIncidentDrawer(incident)
              }
            }}
            priority={toCardPriority(incident.priority)}
            role='button'
            status={toCardStatus(incident.status)}
            tabIndex={0}
            timestamp={formatDateTime(incident.createdAt)}
            title={incident.name ?? incident.displayId ?? ''}
          />
        )
      })}
    </VStack>
  )
}
